import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../../services/api";
import { toast } from "react-toastify";

/**
 * Hook personalizado para gestión de notificaciones VPS
 * Maneja todas las llamadas API y estados
 */
const useVPSNotifications = () => {
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Obtener configuración de notificaciones VPS
   */
  const fetchConfig = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.get("/vps/notifications/config");
      if (isMountedRef.current) {
        if (data.success) {
          setConfig(data.config || null);
        } else {
          throw new Error(data.message || "Error obteniendo configuración de notificaciones");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo configuración de notificaciones:", err);
        // No mostrar toast aquí, el componente decidirá cuándo mostrar
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  /**
   * Guardar configuración de notificaciones VPS
   */
  const saveConfig = useCallback(async (configData) => {
    if (!isMountedRef.current) return null;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.post("/vps/notifications/config", configData);
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(data.message || "Configuración guardada exitosamente");
          await fetchConfig(); // Refrescar configuración
          return data;
        } else {
          throw new Error(data.message || "Error guardando configuración");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error guardando configuración:", err);
        toast.error(errorMessage);
      }
      return null;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [fetchConfig]);

  /**
   * Probar conexión de email
   * Ahora solo requiere smtpConfigId
   */
  const testEmailConnection = useCallback(async (emailData) => {
    if (!isMountedRef.current) return { success: false, message: "" };
    try {
      if (isMountedRef.current) setTestingEmail(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.post("/vps/notifications/test/email", emailData);
      if (isMountedRef.current) {
        return {
          success: data.success || false,
          message: data.message || "Resultado de prueba de email"
        };
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error probando conexión email:", err);
        return { success: false, message: errorMessage };
      }
    } finally {
      if (isMountedRef.current) setTestingEmail(false);
    }
    return { success: false, message: "" };
  }, []);

  /**
   * Probar conexión de WhatsApp
   * Ahora solo requiere whatsappId
   */
  const testWhatsAppConnection = useCallback(async (whatsappData) => {
    if (!isMountedRef.current) return { success: false, message: "" };
    try {
      if (isMountedRef.current) setTestingWhatsApp(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.post("/vps/notifications/test/whatsapp", whatsappData);
      if (isMountedRef.current) {
        return {
          success: data.success || false,
          message: data.message || "Resultado de prueba de WhatsApp"
        };
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error probando conexión WhatsApp:", err);
        return { success: false, message: errorMessage };
      }
    } finally {
      if (isMountedRef.current) setTestingWhatsApp(false);
    }
    return { success: false, message: "" };
  }, []);

  /**
   * Enviar notificación de prueba
   * @param formData - Datos del formulario para enviar prueba sin guardar (opcional)
   */
  const sendTestNotification = useCallback(async (formData = null) => {
    if (!isMountedRef.current) return { success: false, sent: {}, errors: [] };
    try {
      if (isMountedRef.current) setSendingTest(true);
      if (isMountedRef.current) setError(null);
      
      // Enviar datos del formulario si se proporcionan (permite pruebas sin guardar)
      const { data } = await api.post("/vps/notifications/send/test", formData || {});
      
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(data.message || "Notificación de prueba enviada exitosamente");
        } else {
          toast.warning(data.message || "Error enviando notificación de prueba");
        }
        return {
          success: data.success || false,
          sent: data.sent || {},
          errors: data.errors || []
        };
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error enviando notificación de prueba:", err);
        toast.error(errorMessage);
        return { success: false, sent: {}, errors: [errorMessage] };
      }
    } finally {
      if (isMountedRef.current) setSendingTest(false);
    }
    return { success: false, sent: {}, errors: [] };
  }, []);

  // Cargar configuración al montar
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    loading,
    error,
    config,
    testingEmail,
    testingWhatsApp,
    sendingTest,
    fetchConfig,
    saveConfig,
    testEmailConnection,
    testWhatsAppConnection,
    sendTestNotification
  };
};

export default useVPSNotifications;
